import React, { FC } from 'react';
import ReactDOM from 'react-dom';
import { Container } from 'inversify';
import singleSpaReact from 'single-spa-react';
import { I18nextProvider } from 'react-i18next';
import { Router } from 'react-router-dom';
import { createBrowserHistory, History } from 'history';
import { ErrorBoundary } from 'react-error-boundary';

import { createIOC } from './bindings';
import { iocNames } from './iocNames';
import { RootProps, RootUpdatable } from './types/RootUpdatable';
import { I18Service } from './services/I18Service';
import { DepositModal } from './components/DepositModal';
import { SomethingWentWrongModal } from './components/SomethingWentWrongModal';
import { iocContext } from './hooks/useInject';
import { ApolloClient, ApolloProvider, NormalizedCacheObject } from '@apollo/client';

let ioc: Container;

const RootComponent: FC<RootProps> = ({ closeModal, onSuccessButtonClick }) => {
  const { i18next } = ioc.get<I18Service>(iocNames.i18service);
  const history = ioc.get<History>(iocNames.history);
  const apolloClient = ioc.get<ApolloClient<NormalizedCacheObject>>(iocNames.apolloClient);

  return (
    <ErrorBoundary
      FallbackComponent={SomethingWentWrongModal}
      onReset={() => {
        // reset the state of your app so the error doesn't happen again
      }}
    >
      <iocContext.Provider value={ioc}>
        <ApolloProvider client={apolloClient}>
          <I18nextProvider i18n={i18next}>
            <Router history={history}>
              <DepositModal
                onRequestClose={closeModal}
                onSuccessButtonClick={onSuccessButtonClick}
              />
            </Router>
          </I18nextProvider>
        </ApolloProvider>
      </iocContext.Provider>
    </ErrorBoundary>
  );
};

function updateIOC(props: RootProps) {
  const services = ioc.getAll<RootUpdatable>(iocNames.rootUpdatable);
  services.forEach((service) => {
    service.update(props);
  });
}

const reactLifecycles = singleSpaReact({
  React,
  ReactDOM,
  rootComponent: RootComponent,
});

export type Props = RootProps;

export const bootstrap = reactLifecycles.bootstrap;

export const mount = (props: RootProps) => {
  const { getTokens, baseUrl, appConfig, locale } = props;
  const history = createBrowserHistory({ basename: baseUrl });

  ioc = createIOC({ history, getTokens, locale, appConfig });

  updateIOC(props);

  return reactLifecycles.mount(props);
};

export const unmount = reactLifecycles.unmount;

export const update = (props: RootProps) => {
  updateIOC(props);

  return reactLifecycles.update!(props);
};
